import React, { useState, useMemo, useCallback, useEffect } from "react";
import { Tooltip } from "@material-ui/core";
import { useMobileDetection } from "../../utils/deviceDetection";
import { getSectorColors } from "../../utils/stockCardUtils";
import CardBuySellOverlay from "./CardBuySellOverlay";
import MobileCardOverlay from "./MobileCardOverlay";
import OwnershipTokens from "./OwnershipTokens";

const IndexCardView = ({ 
  ix, 
  styleCardSize, 
  activeEvents = [], 
  visualEffects = [],
  onPurchase,
  onSell,
  canPurchase,
  canSell,
  ownedIndex,
  indexOwnershipByPlayer = {},
  playerColors = {},
  playerEmojis = {},
  roundNumber = 1,
  roi = null,
  myTurn = false,
  actionMode = null,
  selectedActionCard = null,
  onApplyAction = null
}) => {
  const emoji = useMemo(() => ix?.emoji || "📈", [ix?.emoji]);
  
  // Use same size as stock cards (no scaling)
  const indexCardSize = useMemo(() => ({
    width: styleCardSize.width || "200px",
    height: styleCardSize.height || "280px"
  }), [styleCardSize]);
  
  const colors = useMemo(() => getSectorColors(ix?.name), [ix?.name]);

  const [isHovered, setIsHovered] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Detect mobile viewport
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handlePurchase = useCallback(() => {
    if (onPurchase && canPurchase) {
      onPurchase(ix);
    }
  }, [onPurchase, canPurchase, ix]);

  const handleSell = useCallback(() => {
    if (onSell && canSell && ownedIndex) {
      onSell(ownedIndex);
    }
  }, [onSell, canSell, ownedIndex]);

  const handleApplyAction = useCallback(() => {
    // Rug Pull can no longer target indexes; no-op for safety.
    return;
  }, []);
  
  const netEffect = useMemo(() => {
    if (!ix || visualEffects.length === 0) return 0;
    return visualEffects
      .filter(effect => effect.indexName === ix.name)
      .reduce((sum, effect) => sum + (effect.priceChange || 0), 0);
  }, [ix, visualEffects]);
  
  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);

  // Rug Pull can only target stocks you own, not indexes
  const isEligibleTarget = false;

  const cardStyle = useMemo(() => ({
    ...indexCardSize,
    background: colors.bg,
    ...(isEligibleTarget ? {} : { borderColor: colors.border })
  }), [indexCardSize, colors.bg, colors.border, isEligibleTarget]);

  const headerStyle = useMemo(() => ({
    background: colors.header,
    borderBottom: `2px solid ${colors.headerBorder}`
  }), [colors.header, colors.headerBorder]);
  
  return (
    <div 
      className="index-card-container"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div 
        className={`stock-card card ${isEligibleTarget ? 'stock-card-eligible-target' : ''}`}
        style={cardStyle}
      >
        {ix ? (
          <>
            <Tooltip title={`${ix.displayValue || ix.name} Index - Current price: $${ix.price}`} arrow placement="top">
              <div className="stock-card-top" style={headerStyle}>
                {(ix.displayValue || ix.name).toUpperCase()}
              </div>
            </Tooltip>
            <div className="stock-card-spacer index-card-emoji-container">
              {emoji}
            </div>
            <div className="stock-card-bottom">
              <div className="stock-card-stats-row">
                <div className="stock-card-dividend" style={{ cursor: 'help' }}>
                  🪙 {ix?.name === 'tech' || ix?.name === 'Health & Science' ? '10%' : '30%'}
                </div>
                <div className="stock-card-growth" style={{ cursor: 'help' }}>📈 📰</div>
              </div>
              <div className="stock-card-cost-bar" data-price={ix.price}>
                <span className="stock-card-price-value">{isMobile ? ix.price : `💲${ix.price}`}</span>
                {netEffect > 0 && (
                  <span className="stock-card-price-change stock-card-price-change-positive">
                    ▲<span className="stock-card-price-change-value"> +{netEffect}</span>
                  </span>
                )}
                {netEffect < 0 && (
                  <span className="stock-card-price-change stock-card-price-change-negative">
                    ▼<span className="stock-card-price-change-value"> {netEffect}</span>
                  </span>
                )}
              </div>
            </div>

            {/* Overlay - Mobile uses dialog, Desktop uses hover overlay */}
            {isMobile ? (
              <MobileCardOverlay
                cardName={ix?.name || "Index"}
                onPurchase={handlePurchase}
                onSell={handleSell}
                canPurchase={canPurchase}
                canSell={canSell && ownedIndex}
                buyPrice={ix.price}
                sellPrice={ix.price}
                roi={roi}
                isMyTurn={myTurn}
              />
            ) : (
            <CardBuySellOverlay
              isHovered={isHovered}
              onPurchase={handlePurchase}
              onSell={handleSell}
              canPurchase={canPurchase}
              canSell={canSell && ownedIndex}
              buyPrice={ix.price}
              sellPrice={ix.price}
              roi={roi}
              isMyTurn={myTurn}
              selectedActionCard={selectedActionCard}
              actionMode={actionMode}
              onApplyAction={null}
            />
            )}
          </>
        ) : null}
      </div>

      <OwnershipTokens
        ownershipByPlayer={indexOwnershipByPlayer}
        itemName={ix?.name}
        playerColors={playerColors}
        playerEmojis={playerEmojis}
        roundNumber={roundNumber}
      />
    </div>
  );
};

export default IndexCardView;